import { isEqual, omit } from 'es-toolkit';

import type { QuickStudyConfig } from '../misc/data-manager';

/**
 * 헤더 단축 버튼으로 노출되는 스터디.
 * 여기 있는 것만 전 심볼 공유 설정(`quickStudies`)의 대상이고, 나머지 지표는 심볼별 `template`이 관리한다.
 */
export const ShortCutStudyMapper: Record<string, { label: string; inputs?: { length: number } }> = {
  'Bollinger Bands': { label: 'BB' },
  'Moving Average': { label: 'MA15', inputs: { length: 15 } },
  'Relative Strength Index': { label: 'RSI' },
};

export const ShortCutStudies = Object.entries(ShortCutStudyMapper).map(([name, values]) => ({ name, ...values }));

export interface QuickStudyPlan {
  toAdd: string[];
  toRemove: string[];
}

/**
 * 전역 `quickStudies` 설정과 현재 심볼 템플릿의 스터디 목록을 비교해 차트에 반영할 차이를 계산한다.
 * - enabled: true + 템플릿에 없음 → 추가 (전역 토글이 새 심볼로 전파)
 * - enabled: false + 템플릿에 있음 → 제거
 * - 설정 자체가 없음 → 심볼별 template이 결정하므로 터치하지 않음
 */
export function resolveQuickStudyPlan(
  storedQuickStudies: Record<string, QuickStudyConfig>,
  templateStudyNames: Set<string>,
): QuickStudyPlan {
  const toAdd: string[] = [];
  const toRemove: string[] = [];

  for (const quickStudyName of Object.keys(ShortCutStudyMapper)) {
    const storedQuickStudy = storedQuickStudies[quickStudyName];
    if (!storedQuickStudy) continue;

    if (storedQuickStudy.enabled && !templateStudyNames.has(quickStudyName)) {
      toAdd.push(quickStudyName);
    } else if (!storedQuickStudy.enabled && templateStudyNames.has(quickStudyName)) {
      toRemove.push(quickStudyName);
    }
  }

  return { toAdd, toRemove };
}

/** 헤더 단축 스터디 토글을 전역 설정에 반영한다. 키는 스터디 이름뿐 — 심볼은 키에 들어가지 않는다. */
export function applyQuickStudyToggle(
  prev: Record<string, QuickStudyConfig>,
  studyName: string,
  enabled: boolean,
): Record<string, QuickStudyConfig> {
  const shortCut = ShortCutStudyMapper[studyName];
  if (!shortCut) return prev;

  return { ...prev, [studyName]: { label: shortCut.label, enabled, inputs: shortCut.inputs } };
}

/**
 * 지표 템플릿을 심볼 버킷에 반영한다. `template`이 null이면 해당 심볼 버킷만 제거한다.
 * 변화가 없으면 이전 레코드를 그대로 반환해 불필요한 스토리지 쓰기를 막는다.
 */
export function applyTemplateForSymbol<T extends object>(
  prev: Record<string, T>,
  symbol: string,
  template: T | null,
): Record<string, T> {
  if (template == null) {
    if (!(symbol in prev)) return prev;
    return omit(prev, [symbol]);
  }
  if (isEqual(prev[symbol], template)) return prev;
  return { ...prev, [symbol]: template };
}
